// Raise-ticket dialog — creates or links a Jira issue for a discussion item
// or visit action. Items: POST /visits/items/{raise|link}. Actions: same under
// /visits/actions/… (WP-H — accepting an action IS raising/linking it).
import { useState } from "react";
import { useSnackbar } from "notistack";
import { Modal, SelectField, TextField, TextArea, Button, Alert } from "../../design/ui";
import {
  raiseItemTicket, linkItemTicket, raiseActionTicket, linkActionTicket,
  type DiscussionItem, type VisitAction, type RaiseTarget,
} from "../../lib/brain-api";

const TARGET_OPTIONS: { value: RaiseTarget; label: string }[] = [
  { value: "support", label: "Support" },
  { value: "feature-request", label: "Feature request" },
  { value: "dev", label: "Dev" },
];

const PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "— none —" },
  { value: "Highest", label: "Highest" },
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
  { value: "Lowest", label: "Lowest" },
];

export type RaiseSubject =
  | { kind: "item"; item: DiscussionItem }
  | { kind: "action"; action: VisitAction };

function subjectTitle(subject: RaiseSubject): string {
  if (subject.kind === "item") return `Raise ticket — item #${subject.item.n}`;
  return `Raise ticket — action`;
}

function defaultSummary(subject: RaiseSubject): string {
  return subject.kind === "item" ? subject.item.topic : subject.action.text;
}

function defaultDescription(subject: RaiseSubject): string {
  if (subject.kind === "item") return subject.item.notes ?? "";
  return subject.action.evidence ?? "";
}

function defaultTargetFromSubject(subject: RaiseSubject, fallback: RaiseTarget): RaiseTarget {
  if (subject.kind === "action" && subject.action.suggestedTarget) {
    const t = subject.action.suggestedTarget;
    if (t === "support" || t === "feature-request" || t === "dev") return t;
  }
  return fallback;
}

export function RaiseTicketDialog({
  customerId, visitId, subject, defaultTarget, onClose, onChanged,
}: {
  customerId: string;
  visitId: string;
  subject: RaiseSubject;
  defaultTarget: RaiseTarget;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [target, setTarget] = useState<RaiseTarget>(() => defaultTargetFromSubject(subject, defaultTarget));
  const [priority, setPriority] = useState("");
  const [summary, setSummary] = useState(() => defaultSummary(subject));
  const [description, setDescription] = useState(() => defaultDescription(subject));
  const [submitting, setSubmitting] = useState(false);
  const [linkKey, setLinkKey] = useState("");
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = submitting || linking;

  const submit = async () => {
    if (!summary.trim() || !description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      if (subject.kind === "item") {
        const result = await raiseItemTicket({
          customerId,
          visitId,
          itemId: subject.item.id,
          target,
          summary: summary.trim(),
          description: description.trim(),
          ...(priority ? { priority } : {}),
        });
        if (!result.item) {
          enqueueSnackbar(`Created ${result.key} — item wasn't updated (rare race), refresh to check`, { variant: "warning" });
        } else {
          enqueueSnackbar(`Created ${result.key}`, { variant: "success" });
        }
      } else {
        const result = await raiseActionTicket({
          customerId,
          visitId,
          actionId: subject.action.id,
          target,
          summary: summary.trim(),
          description: description.trim(),
          ...(priority ? { priority } : {}),
        });
        if (!result.action) {
          enqueueSnackbar(`Created ${result.key} — action wasn't updated (rare race), refresh to check`, { variant: "warning" });
        } else {
          enqueueSnackbar(`Created ${result.key}`, { variant: "success" });
        }
      }
      onChanged();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't create the ticket — try again";
      setError(msg);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const submitLink = async () => {
    const key = linkKey.trim().toUpperCase();
    if (!key) return;
    setLinking(true);
    setError(null);
    try {
      if (subject.kind === "item") {
        await linkItemTicket({ itemId: subject.item.id, jiraKey: key });
      } else {
        await linkActionTicket({ actionId: subject.action.id, jiraKey: key });
      }
      enqueueSnackbar(`Linked ${key}`, { variant: "success" });
      onChanged();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't link that key — try again";
      setError(msg);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setLinking(false);
    }
  };

  return (
    <Modal
      title={subjectTitle(subject)}
      open
      onClose={onClose}
      width={560}
      actions={
        <>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <TextField
              placeholder="Existing key (e.g. TICKET-123)"
              value={linkKey}
              onChange={(e) => setLinkKey(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void submitLink(); }}
              disabled={busy}
              style={{ width: 190 }}
            />
            <Button size="sm" icon="link" disabled={busy || !linkKey.trim()} onClick={() => void submitLink()}>
              {linking ? "Linking…" : "Link"}
            </Button>
          </div>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            icon={submitting ? undefined : "ticket"}
            disabled={busy || !summary.trim() || !description.trim()}
            onClick={() => void submit()}
          >
            {submitting && <span className="kt-spinner" style={{ width: 13, height: 13, borderWidth: 2, marginRight: 6 }} />}
            {submitting ? "Creating…" : "Create"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {error && <Alert tone="danger">{error}</Alert>}
        <SelectField
          label="Target"
          value={target}
          onChange={(e) => setTarget(e.target.value as RaiseTarget)}
          disabled={busy}
        >
          {TARGET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </SelectField>
        <SelectField
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          disabled={busy}
        >
          {PRIORITY_OPTIONS.map((o) => <option key={o.value || "none"} value={o.value}>{o.label}</option>)}
        </SelectField>
        <TextField
          label="Summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          disabled={busy}
        />
        <TextArea
          label="Description"
          hint="Site-visit attribution is added automatically."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={6}
          disabled={busy}
        />
      </div>
    </Modal>
  );
}
